// @ts-nocheck
/**
 * useProfileManager - Profile state management and persistence hook
 * 
 * Handles:
 * - Profile updates to Firestore
 * - AI analysis result processing and storage
 * - Sample request tracking
 * - Undo functionality for profile changes
 */
import { useCallback, useRef } from 'react';
import { auth, db } from '../services/firebaseConfig';
import { doc, setDoc } from 'firebase/firestore';
import { cleanForFirestore } from '../services/profileService';
import { authFetch } from '../services/apiAuth';
import { SAMPLE_FEATURE_ENABLED } from '../config/adminConfig';
import { getApiBaseUrl } from '../utils/apiConfig';

const API_URL = getApiBaseUrl();
import {
  extractConcernsFromAnalysis,
  mapSkinTypeFromAI,
  generateSearchKeywords,
  mergeConcerns,
} from '../utils/aiAnalysisUtils';

/**
 * Build AI analysis data structure from AILabTools result
 */
export function buildAIAnalysisData(analysisResult, lang) {
  // Extract skin type value - handle both direct number and nested object structure
  const skinTypeValue = typeof analysisResult.skin_type === 'object' 
    ? analysisResult.skin_type?.skin_type 
    : analysisResult.skin_type;
  
  return {
    lastAnalysisDate: new Date().toISOString(),
    
    // Skin type analysis (nullable — Firestore rejects undefined)
    skinType: skinTypeValue ?? null,
    skinTypeDetails: analysisResult.skin_type?.details || analysisResult.skin_type_detail || [],
    
    // Skin issues detected (all nullable — API may omit any field)
    issues: {
      pores: {
        leftCheek: analysisResult.pores_left_cheek ?? null,
        rightCheek: analysisResult.pores_right_cheek ?? null,
        forehead: analysisResult.pores_forehead ?? null,
        jaw: analysisResult.pores_jaw ?? null,
      },
      wrinkles: {
        nasolabialFold: analysisResult.nasolabial_fold ?? null,
        foreheadWrinkle: analysisResult.forehead_wrinkle ?? null,
        eyeFinelines: analysisResult.eye_finelines ?? null,
        crowsFeet: analysisResult.crows_feet ?? null,
        glabellaWrinkle: analysisResult.glabella_wrinkle ?? null,
      },
      other: {
        eyePouch: analysisResult.eye_pouch ?? null,
        darkCircle: analysisResult.dark_circle ?? null,
        skinSpot: analysisResult.skin_spot ?? null,
        acne: analysisResult.acne ?? null,
        blackhead: analysisResult.blackhead ?? null,
      }
    },
    
    // Facial features (nullable — API may omit eyelid detection)
    features: {
      leftEyelids: analysisResult.left_eyelids ?? null,
      rightEyelids: analysisResult.right_eyelids ?? null,
    },
    
    // Skin concerns extracted from analysis
    detectedConcerns: extractConcernsFromAnalysis(analysisResult, lang),
    
    // Google search keywords for product recommendations
    searchKeywords: generateSearchKeywords(analysisResult, lang),
  };
}

/**
 * Custom hook for profile state management
 */
export function useProfileManager({
  masterProfile,
  setMasterProfile,
  showAddressFormForSample,
  setShowAddressFormForSample,
  language,
  t,
}) {
  // Ref to track current profile for AI updates
  const masterProfileRef = useRef(masterProfile);
  masterProfileRef.current = masterProfile;

  /**
   * Update profile to Firestore with sample tracking
   */
  const handleUpdateProfile = useCallback(async (updatedProfile) => {
    const user = auth.currentUser;
    if (!user) {
      console.error('No user logged in');
      return;
    }

    try {
      console.log('📝 Updating profile...', { uid: user.uid });
      
      // Auto-set sample tracking fields when address is first provided
      // (Only when sample feature is enabled)
      if (SAMPLE_FEATURE_ENABLED) {
        const isNewAddress = updatedProfile.logistics?.address1 && 
                            !masterProfile.logistics?.address1;
        
        if (isNewAddress) {
          const now = new Date().toISOString();
          if (!updatedProfile.logistics.sampleRequestDate) {
            updatedProfile.logistics.sampleRequestDate = now;
          }
          if (!updatedProfile.logistics.sampleDeliveryStatus) {
            updatedProfile.logistics.sampleDeliveryStatus = 'pending';
          }
          if (!updatedProfile.segment) {
            updatedProfile.segment = {};
          }
          if (!updatedProfile.segment.customerStage || updatedProfile.segment.customerStage === 'visitor') {
            updatedProfile.segment.customerStage = 'sample-requested';
          }
          console.log('🎁 Auto-set sample tracking fields for new address');
        }
      }
      
      // Update local state
      setMasterProfile(updatedProfile);
      masterProfileRef.current = updatedProfile;
      
      // Clean profile for Firestore (remove undefined/null)
      const cleanedProfile = cleanForFirestore(updatedProfile);
      
      // Save to Firestore
      const profileDocRef = doc(db, 'masterProfiles', user.uid);
      await setDoc(profileDocRef, cleanedProfile, { merge: true });
      
      console.log('✅ Profile updated successfully in Firestore');
      
      // If this was triggered by sample request, proceed with request
      if (showAddressFormForSample) {
        setShowAddressFormForSample(false);
        
        // Send confirmation email
        try {
          const emailResponse = await authFetch(`${API_URL}/sendSampleRequestEmail`, {
            method: 'POST',
            body: JSON.stringify({
              userEmail: user.email,
              userName: cleanedProfile.logistics?.recipientName || cleanedProfile.identity?.name || '',
              address: cleanedProfile.logistics,
              language: language
            })
          });
          
          if (emailResponse.ok) {
            console.log('✅ Confirmation email sent');
          } else {
            console.warn('⚠️ Email sending failed, but sample request was recorded');
          }
        } catch (emailError) {
          console.error('❌ Email API error:', emailError);
        }
        
        // Show success message
        setTimeout(() => {
          alert(t(
            '✅ 샘플 요청이 접수되었습니다!\n\n고객님의 피부에 맞는 제품을 선별하여 보내드리겠습니다.\n확인 이메일을 발송했으니 확인해주세요! 📧',
            '✅ Sample request submitted!\n\nWe will select products matching your skin profile and send them to you.\nPlease check your email for confirmation! 📧'
          ));
        }, 500);
      }
    } catch (error) {
      console.error('❌ Failed to update profile:', error);
      
      // Provide specific error messages based on error code
      let errorMsg;
      if (error.code === 'permission-denied') {
        errorMsg = t(
          '⚠️ Firestore 권한 오류입니다.\n\nFirebase Console에서 Firestore 규칙을 업데이트해주세요.',
          '⚠️ Firestore permission denied.\n\nPlease update Firestore rules in Firebase Console.'
        );
      } else if (error.code === 'unavailable') {
        errorMsg = t(
          '네트워크 오류: Firestore에 연결할 수 없습니다.',
          'Network error: Cannot connect to Firestore.'
        );
      } else {
        errorMsg = t(
          `프로필 업데이트에 실패했습니다: ${error.message}`,
          `Failed to update profile: ${error.message}`
        );
      }
      
      alert(errorMsg);
    }
  }, [masterProfile, showAddressFormForSample, setShowAddressFormForSample, setMasterProfile, language, t]);

  /**
   * Update Master DB with AI skin analysis results
   */
  const updateMasterDBWithAnalysis = useCallback(async (analysisResult, lang) => {
    const user = auth.currentUser;
    if (!user) {
      console.warn('No user logged in, skipping AI analysis update');
      return;
    }

    try {
      const aiData = buildAIAnalysisData(analysisResult, lang);
      
      // Extract skin type for mapping
      const skinTypeValue = typeof analysisResult.skin_type === 'object' 
        ? analysisResult.skin_type?.skin_type 
        : analysisResult.skin_type;
      
      console.log('💾 Updating profile with AI analysis');
      console.log('🔑 Generated search keywords:', aiData.searchKeywords);
      console.log('⚠️ Detected concerns:', aiData.detectedConcerns);

      // Map AI skin type to profile skin type
      const aiSkinType = mapSkinTypeFromAI(skinTypeValue, lang);
      
      // Build updated profile
      const currentProfile = masterProfileRef.current;
      const updatedProfile = {
        ...currentProfile,
        aiAnalysis: aiData,
        skin: {
          ...currentProfile.skin,
          type: aiSkinType || currentProfile.skin?.type,
          concerns: mergeConcerns(
            currentProfile.skin?.concerns || [],
            aiData.detectedConcerns
          ),
        },
        meta: {
          ...currentProfile.meta,
          lastPictureUpload: new Date().toISOString(),
        },
      };
      
      console.log('🔍 Skin type update:', {
        extracted: skinTypeValue,
        mapped: aiSkinType,
        previous: currentProfile.skin?.type,
        final: aiSkinType || currentProfile.skin?.type
      });

      // Save to Firestore
      await handleUpdateProfile(updatedProfile);
      console.log('✅ Master DB updated with AI analysis');
    } catch (error) {
      console.error('❌ Failed to update Master DB with AI analysis:', error);
    }
  }, [handleUpdateProfile]);

  return {
    handleUpdateProfile,
    updateMasterDBWithAnalysis,
    masterProfileRef,
  };
}
